import { useState, useMemo, useRef, useEffect } from 'react';
import type { ClubTheme } from './themes';
import type { SocialPost } from './types';
import { useChannels, useClubEvents } from './queries';
import { useSocialFeed } from './useSocialFeed';
import ChannelSidebar from './ChannelSidebar';
import SubscriptionSettings from './SubscriptionSettings';
import PostComposer from './PostComposer';
import PostCard from './PostCard';
import PinnedBanner from './PinnedBanner';
import SystemFlairPost from './SystemFlairPost';
import ConversationList from './ConversationList';
import ConversationThread from './ConversationThread';
import NewConversation from './NewConversation';
import EventCard from './EventCard';
import EventComposer from './EventComposer';
import EventDetailModal from './EventDetailModal';
import MemberDirectory from './MemberDirectory';
import MemberProfileSlideout from './MemberProfileSlideout';
import ModerationPanel from './ModerationPanel';
import MyActivity from './MyActivity';
import BentoDashboard from './BentoDashboard';
import TowerMarquee from './TowerMarquee';
import FocusPostView from './FocusPostView';
import { useRealtimeFeed, useRealtimeConversations } from './useRealtimeFeed';
import { useAuth } from '@/lib/auth-context';

type View = 'feed' | 'dms' | 'events' | 'members';
type DmSubView = 'list' | 'thread' | 'new';

interface SocialShellProps {
  clubId: string;
  theme: ClubTheme;
  canPost?: boolean;
}

export default function SocialShell({ clubId, theme, canPost = true }: SocialShellProps) {
  const { user } = useAuth();
  const [view, setView] = useState<View>('feed');
  const [dmSubView, setDmSubView] = useState<DmSubView>('list');
  const [activeConvId, setActiveConvId] = useState<string | null>(null);
  const [activeConvName, setActiveConvName] = useState<string>('');

  const [feedMode, setFeedMode] = useState<'bento' | 'scroll'>('bento');
  const [activeChannel, setActiveChannel] = useState<string | null>(null);
  const [showEventComposer, setShowEventComposer] = useState(false);
  const [eventsFilter, setEventsFilter] = useState<'upcoming' | 'past'>('upcoming');
  const [showModPanel, setShowModPanel] = useState(false);
  const [showSubSettings, setShowSubSettings] = useState(false);
  const [showMyActivity, setShowMyActivity] = useState(false);
  const [activeEventId, setActiveEventId] = useState<string | null>(null);
  const [quotingPost, setQuotingPost] = useState<SocialPost | null>(null);
  const [focusPost, setFocusPost] = useState<SocialPost | null>(null);
  const [viewingMemberId, setViewingMemberId] = useState<string | null>(null);
  const composerRef = useRef<HTMLDivElement>(null);

  // Supabase Realtime — auto-refresh feed, DMs, and unread badges
  useRealtimeFeed(clubId);
  useRealtimeConversations(clubId);

  const { data: channels = [] } = useChannels(clubId);
  const { posts, loading, error, loadMore, refresh, hasMore } = useSocialFeed(clubId, activeChannel);
  const { data: events = [], isLoading: eventsLoading, refetch: refetchEvents } = useClubEvents(clubId, eventsFilter);

  // Separate pinned posts from the feed
  const pinnedPosts = useMemo(() => posts.filter((p) => p.is_pinned), [posts]);

  // Detect if current user might be a moderator (admin/superadmin/teacher/coach/sponsor)
  // The real check happens server-side; this is just for showing the mod button
  const platformRole = (user as any)?.user_metadata?.role || '';
  const isMaybemod = ['admin', 'superadmin', 'teacher', 'coach'].includes(platformRole);

  const openThread = (convId: string, name?: string) => {
    setActiveConvId(convId);
    setActiveConvName(name || '');
    setDmSubView('thread');
  };

  const handleQuote = (post: SocialPost) => {
    setQuotingPost(post);
    setView('feed');
    // Scroll to composer
    setTimeout(() => composerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 100);
  };

  const handleMessageUser = (userId: string) => {
    // Switch to DMs and start new conversation
    setView('dms');
    setDmSubView('new');
  };

  const viewLabels: Record<View, string> = {
    feed: 'Feed',
    events: 'Events',
    members: 'Members',
    dms: 'Messages',
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: theme.colors.bg }}>
      {/* Header */}
      <header
        className="sticky top-0 z-10 border-b px-3 py-3 md:px-6 md:py-4"
        style={{ backgroundColor: theme.colors.surface, borderColor: `${theme.colors.muted}20` }}
      >
        <div className="mx-auto flex max-w-6xl flex-col gap-2 md:flex-row md:items-center md:justify-between">
          <div className="shrink-0">
            <h1 className="text-lg md:text-xl font-bold" style={{ color: theme.colors.text }}>
              {theme.name}
            </h1>
            <p className="text-[10px] md:text-xs" style={{ color: theme.colors.muted }}>
              {theme.metaphor}
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-2 md:gap-3">
            {/* View toggle */}
            <div className="flex rounded-xl overflow-hidden" style={{ border: `1px solid ${theme.colors.muted}30` }}>
              {(['feed', 'events', 'members', 'dms'] as const).map((v) => (
                <button
                  key={v}
                  type="button"
                  onClick={() => { setView(v); if (v === 'dms') setDmSubView('list'); }}
                  className="px-2.5 py-1 md:px-4 md:py-1.5 text-[11px] md:text-xs font-semibold transition-colors"
                  style={{
                    backgroundColor: view === v ? theme.colors.primary : 'transparent',
                    color: view === v ? theme.colors.bg : theme.colors.muted,
                  }}
                >
                  {viewLabels[v]}
                </button>
              ))}
            </div>

            {/* Bento / Full Feed toggle (only when on feed view) */}
            {view === 'feed' && (
              <button
                type="button"
                onClick={() => setFeedMode(feedMode === 'bento' ? 'scroll' : 'bento')}
                className="rounded-lg px-2 py-1.5 text-[10px] font-bold uppercase tracking-wider transition-colors"
                style={{
                  backgroundColor: feedMode === 'scroll' ? `${theme.colors.primary}20` : `${theme.colors.muted}10`,
                  color: feedMode === 'scroll' ? theme.colors.primary : theme.colors.muted,
                }}
              >
                {feedMode === 'bento' ? 'Full Feed' : 'Dashboard'}
              </button>
            )}

            {/* My Activity toggle */}
            <button
              type="button"
              onClick={() => setShowMyActivity(!showMyActivity)}
              className="rounded-lg px-2 py-1.5 text-[10px] font-bold uppercase tracking-wider transition-colors"
              style={{
                backgroundColor: showMyActivity ? `${theme.colors.primary}20` : `${theme.colors.muted}10`,
                color: showMyActivity ? theme.colors.primary : theme.colors.muted,
              }}
            >
              Activity
            </button>

            {/* Mod panel toggle */}
            {isMaybemod && (
              <button
                type="button"
                onClick={() => setShowModPanel(!showModPanel)}
                className="rounded-lg px-2 py-1.5 text-[10px] font-bold uppercase tracking-wider transition-colors"
                style={{
                  backgroundColor: showModPanel ? '#EF444420' : `${theme.colors.muted}10`,
                  color: showModPanel ? '#EF4444' : theme.colors.muted,
                }}
              >
                Mod
              </button>
            )}
          </div>
        </div>
      </header>

      {/* Moderation panel (slide-in) */}
      {showModPanel && (
        <div className="mx-auto max-w-3xl px-6 py-4">
          <div
            className="rounded-2xl p-4"
            style={{ backgroundColor: theme.colors.surface, border: `1px solid ${theme.colors.muted}20` }}
          >
            <ModerationPanel clubId={clubId} theme={theme} />
          </div>
        </div>
      )}

      {/* My Activity panel */}
      {showMyActivity && (
        <div className="mx-auto max-w-3xl px-6 py-4">
          <MyActivity
            clubId={clubId}
            theme={theme}
            onNavigateToPost={(postId) => {
              setShowMyActivity(false);
              setView('feed');
              setTimeout(() => {
                const el = document.getElementById(`post-${postId}`);
                if (el) {
                  el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                  el.style.outline = `2px solid ${theme.colors.primary}`;
                  setTimeout(() => { el.style.outline = ''; }, 2000);
                }
              }, 200);
            }}
          />
        </div>
      )}

      {/* Feed view */}
      {view === 'feed' && (
        <div className="mx-auto max-w-6xl px-6 py-6">
          {/* Tower Marquee — system announcements banner */}
          <TowerMarquee clubId={clubId} theme={theme} />

          {/* Bento Dashboard mode */}
          {feedMode === 'bento' && (
            <div className="space-y-4">
              {canPost && (
                <div ref={composerRef}>
                  <PostComposer
                    clubId={clubId}
                    channels={channels}
                    theme={theme}
                    quotedPost={quotingPost}
                    onClearQuote={() => setQuotingPost(null)}
                    onPosted={refresh}
                  />
                </div>
              )}
              <BentoDashboard
                clubId={clubId}
                theme={theme}
                onViewArchive={() => setFeedMode('scroll')}
                onPostClick={(post) => setFocusPost(post)}
                onRefresh={refresh}
                onQuote={handleQuote}
                onNavigateToPost={(postId) => {
                  // Find the post in the feed and open Focus Mode
                  const found = posts.find((p) => p.id === postId);
                  if (found) setFocusPost(found);
                }}
              />
            </div>
          )}

          {/* Full scroll mode (classic feed) */}
          {feedMode === 'scroll' && (
          <div className="flex gap-6">
          {/* Sidebar */}
          <aside className="hidden w-56 shrink-0 lg:block">
            <div
              className="sticky top-24 rounded-2xl p-4"
              style={{ backgroundColor: theme.colors.surface, border: `1px solid ${theme.colors.muted}20` }}
            >
              <h2
                className="mb-3 text-xs font-bold uppercase tracking-widest"
                style={{ color: theme.colors.muted }}
              >
                Channels
              </h2>
              <ChannelSidebar
                channels={channels}
                activeSlug={activeChannel}
                onSelect={setActiveChannel}
                theme={theme}
                clubId={clubId}
              />

              {/* Subscription settings toggle */}
              <button
                type="button"
                onClick={() => setShowSubSettings(!showSubSettings)}
                className="mt-3 w-full text-left text-[10px] font-medium uppercase tracking-wider"
                style={{ color: theme.colors.muted }}
              >
                {showSubSettings ? 'Hide' : 'Notification'} Settings
              </button>

              {showSubSettings && (
                <div className="mt-2">
                  <SubscriptionSettings clubId={clubId} theme={theme} />
                </div>
              )}
            </div>
          </aside>

          {/* Feed */}
          <main className="flex-1 min-w-0 space-y-4">
            {canPost && (
              <div ref={composerRef}>
                <PostComposer
                  clubId={clubId}
                  channels={channels}
                  theme={theme}
                  quotedPost={quotingPost}
                  onClearQuote={() => setQuotingPost(null)}
                  onPosted={refresh}
                />
              </div>
            )}

            {/* Mobile channel selector */}
            <div className="flex gap-2 overflow-x-auto pb-2 lg:hidden">
              <button
                type="button"
                onClick={() => setActiveChannel(null)}
                className="shrink-0 rounded-full px-3 py-1 text-xs font-medium"
                style={{
                  backgroundColor: activeChannel === null ? theme.colors.primary : `${theme.colors.muted}15`,
                  color: activeChannel === null ? theme.colors.bg : theme.colors.muted,
                }}
              >
                All
              </button>
              {channels.map((ch) => (
                <button
                  key={ch.slug}
                  type="button"
                  onClick={() => setActiveChannel(ch.slug)}
                  className="shrink-0 rounded-full px-3 py-1 text-xs font-medium"
                  style={{
                    backgroundColor: activeChannel === ch.slug ? theme.colors.primary : `${theme.colors.muted}15`,
                    color: activeChannel === ch.slug ? theme.colors.bg : theme.colors.muted,
                  }}
                >
                  {ch.name}
                </button>
              ))}
            </div>

            {/* Pinned posts banner */}
            {pinnedPosts.length > 0 && (
              <PinnedBanner posts={pinnedPosts} theme={theme} />
            )}

            {loading && posts.length === 0 && (
              <div className="py-12 text-center">
                <div
                  className="inline-block h-8 w-8 animate-spin rounded-full border-2 border-t-transparent"
                  style={{ borderColor: `${theme.colors.muted}40`, borderTopColor: theme.colors.primary }}
                />
              </div>
            )}

            {error && (
              <div className="rounded-xl p-4 text-center text-sm text-red-400" style={{ backgroundColor: theme.colors.surface }}>
                {error}
              </div>
            )}

            {!loading && !error && posts.length === 0 && (
              <div
                className="rounded-2xl p-8 text-center"
                style={{ backgroundColor: theme.colors.surface, border: `1px dashed ${theme.colors.muted}40` }}
              >
                <p className="text-lg font-semibold" style={{ color: theme.colors.text }}>
                  Welcome to {theme.metaphor}
                </p>
                <p className="mt-1 text-sm" style={{ color: theme.colors.muted }}>
                  {canPost ? 'Be the first to share something.' : 'Posts from your coaches will appear here.'}
                </p>
              </div>
            )}

            {posts.map((post) =>
              post.author_type === 'system' && post.post_type !== 'post' ? (
                <SystemFlairPost key={post.id} post={post} theme={theme} />
              ) : (
                <PostCard
                  key={post.id}
                  post={post}
                  theme={theme}
                  onRefresh={refresh}
                  onQuote={handleQuote}
                  onViewMember={setViewingMemberId}
                  onNavigateToPost={(postId) => {
                    const el = document.getElementById(`post-${postId}`);
                    if (el) {
                      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                      el.style.outline = `2px solid ${theme.colors.primary}`;
                      setTimeout(() => { el.style.outline = ''; }, 2000);
                    }
                  }}
                />
              ),
            )}

            {hasMore && (
              <div className="py-4 text-center">
                <button
                  type="button"
                  onClick={loadMore}
                  className="rounded-xl px-6 py-2 text-sm font-medium transition-colors"
                  style={{ backgroundColor: `${theme.colors.primary}20`, color: theme.colors.primary }}
                >
                  Load more
                </button>
              </div>
            )}
          </main>
          </div>
          )}
        </div>
      )}

      {/* Events view */}
      {view === 'events' && (
        <div className="mx-auto max-w-3xl px-6 py-6 space-y-4">
          {/* Filter + create button */}
          <div className="flex items-center justify-between">
            <div className="flex gap-2">
              {(['upcoming', 'past'] as const).map((f) => (
                <button
                  key={f}
                  type="button"
                  onClick={() => setEventsFilter(f)}
                  className="rounded-full px-3 py-1 text-xs font-medium capitalize transition-colors"
                  style={{
                    backgroundColor: eventsFilter === f ? theme.colors.primary : `${theme.colors.muted}15`,
                    color: eventsFilter === f ? theme.colors.bg : theme.colors.muted,
                  }}
                >
                  {f}
                </button>
              ))}
            </div>
            {canPost && (
              <button
                type="button"
                onClick={() => setShowEventComposer(!showEventComposer)}
                className="rounded-lg px-3 py-1.5 text-xs font-semibold transition-colors"
                style={{ backgroundColor: theme.colors.primary, color: theme.colors.bg }}
              >
                {showEventComposer ? 'Cancel' : 'New Event'}
              </button>
            )}
          </div>

          {/* Event composer */}
          {showEventComposer && (
            <EventComposer
              clubId={clubId}
              channels={channels}
              theme={theme}
              onCreated={() => { setShowEventComposer(false); refetchEvents(); }}
              onCancel={() => setShowEventComposer(false)}
            />
          )}

          {/* Loading */}
          {eventsLoading && (
            <div className="py-12 text-center">
              <div
                className="inline-block h-8 w-8 animate-spin rounded-full border-2 border-t-transparent"
                style={{ borderColor: `${theme.colors.muted}40`, borderTopColor: theme.colors.primary }}
              />
            </div>
          )}

          {/* Empty state */}
          {!eventsLoading && events.length === 0 && (
            <div
              className="rounded-2xl p-8 text-center"
              style={{ backgroundColor: theme.colors.surface, border: `1px dashed ${theme.colors.muted}40` }}
            >
              <p className="text-lg font-semibold" style={{ color: theme.colors.text }}>
                {eventsFilter === 'upcoming' ? 'No upcoming events' : 'No past events'}
              </p>
              <p className="mt-1 text-sm" style={{ color: theme.colors.muted }}>
                {canPost ? 'Create the first event for the club.' : 'Events will appear here when scheduled.'}
              </p>
            </div>
          )}

          {/* Event cards */}
          {events.map((event) => (
            <EventCard
              key={event.id}
              event={event}
              clubId={clubId}
              theme={theme}
              onViewDetails={(id) => setActiveEventId(id)}
            />
          ))}
        </div>
      )}

      {/* Event detail modal */}
      {activeEventId && (
        <EventDetailModal
          clubId={clubId}
          eventId={activeEventId}
          theme={theme}
          onClose={() => setActiveEventId(null)}
        />
      )}

      {/* Members view */}
      {view === 'members' && (
        <div className="mx-auto max-w-3xl px-6 py-6">
          <MemberDirectory
            clubId={clubId}
            theme={theme}
            onMessageUser={handleMessageUser}
            onViewMember={setViewingMemberId}
          />
        </div>
      )}

      {/* DM view */}
      {view === 'dms' && (
        <div className="mx-auto flex max-w-4xl gap-0 lg:gap-6 px-0 lg:px-6 py-0 lg:py-6" style={{ height: 'calc(100vh - 73px)' }}>
          {/* Conversation list — always visible on desktop, hidden when in thread on mobile */}
          <div
            className={`w-full lg:w-80 shrink-0 overflow-hidden rounded-none lg:rounded-2xl ${
              dmSubView !== 'list' ? 'hidden lg:block' : ''
            }`}
            style={{ backgroundColor: theme.colors.surface, border: `1px solid ${theme.colors.muted}20` }}
          >
            <ConversationList
              clubId={clubId}
              theme={theme}
              activeConvId={activeConvId}
              onSelect={(id) => openThread(id)}
              onNewConversation={() => setDmSubView('new')}
            />
          </div>

          {/* Thread / New conversation panel */}
          <div
            className={`flex-1 min-w-0 overflow-hidden rounded-none lg:rounded-2xl ${
              dmSubView === 'list' ? 'hidden lg:flex' : 'flex'
            } flex-col`}
            style={{ backgroundColor: theme.colors.surface, border: `1px solid ${theme.colors.muted}20` }}
          >
            {dmSubView === 'thread' && activeConvId && (
              <ConversationThread
                clubId={clubId}
                conversationId={activeConvId}
                theme={theme}
                partnerName={activeConvName}
                onBack={() => setDmSubView('list')}
              />
            )}

            {dmSubView === 'new' && (
              <NewConversation
                clubId={clubId}
                theme={theme}
                onCreated={(convId) => openThread(convId)}
                onCancel={() => setDmSubView('list')}
              />
            )}

            {dmSubView === 'list' && (
              <div className="flex flex-1 items-center justify-center">
                <p className="text-sm" style={{ color: theme.colors.muted }}>
                  Select a conversation
                </p>
              </div>
            )}
          </div>
        </div>
      )}
      {/* Focus Mode overlay */}
      {focusPost && (
        <FocusPostView
          post={focusPost}
          clubId={clubId}
          theme={theme}
          onClose={() => setFocusPost(null)}
        />
      )}

      {/* Member Profile Slideout */}
      {viewingMemberId && (
        <MemberProfileSlideout
          userId={viewingMemberId}
          clubId={clubId}
          currentUserId={user?.id}
          theme={theme}
          onClose={() => setViewingMemberId(null)}
          onMessage={handleMessageUser}
        />
      )}
    </div>
  );
}
